import { create } from 'zustand';

import type { TapToPayErrorCode } from './tap-to-pay-errors';
import type { TapToPayConnectionAuthorization } from './tap-to-pay/types';
import {
  readTapToPaySetupRecord,
  type TapToPaySetupIdentity,
  recordTapToPayGuideCompleted,
  recordPointCraftTermsAccepted,
  recordMerchantEducationCompleted,
  POINTCRAFT_TAP_TO_PAY_TERMS_VERSION,
} from './tap-to-pay-setup-storage';

export type { TapToPaySetupIdentity } from './tap-to-pay-setup-storage';

export type TapToPayAuthorizationState = 'authorization-unknown' | 'authorized' | 'unauthorized';
export type TapToPayPersistenceState =
  | 'persistence-not-ready'
  | 'persistence-loading'
  | 'persistence-ready'
  | 'persistence-failed';
export type TapToPayGuideState = 'guide-required' | 'guide-completed';
export type PointCraftTermsState = 'pointcraft-terms-required' | 'pointcraft-terms-accepted';
export type AppleLinkState =
  | 'apple-link-unknown'
  | 'apple-link-required'
  | 'apple-terms-in-progress'
  | 'apple-link-confirmed';
export type EducationState =
  | 'education-not-started'
  | 'education-presenting'
  | 'education-completed'
  | 'education-failed';
export type ReaderState =
  | 'reader-idle'
  | 'reader-preparing'
  | 'reader-configuring'
  | 'reader-ready'
  | 'reader-failed'
  | 'reader-disconnected';

export type TapToPayConfigurationProgress = {
  status:
    | 'idle'
    | 'preparing'
    | 'configuration-started'
    | 'configuration-progress'
    | 'configuration-complete'
    | 'ready'
    | 'failed'
    | 'disconnected'
    | 'reconnecting';
  operationId: string | null;
  progress: number | null;
  normalizedError: TapToPayErrorCode | null;
  readerId: string | null;
  locationId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  readerPrompt: {
    kind: 'display-message' | 'reader-input';
    code: string;
    options: string[];
  } | null;
};

export const EMPTY_TAP_TO_PAY_CONFIGURATION: TapToPayConfigurationProgress = {
  status: 'idle',
  operationId: null,
  progress: null,
  normalizedError: null,
  readerId: null,
  locationId: null,
  startedAt: null,
  completedAt: null,
  readerPrompt: null,
};

type TapToPaySetupState = {
  accountKey: string | null;
  identityKey: string | null;
  identity: TapToPaySetupIdentity;
  authorization: TapToPayAuthorizationState;
  paymentAuthorization: TapToPayAuthorizationState;
  persistence: TapToPayPersistenceState;
  persistenceError: string | null;
  guide: TapToPayGuideState;
  pointcraftTerms: PointCraftTermsState;
  appleLink: AppleLinkState;
  education: EducationState;
  reader: ReaderState;
  configuration: TapToPayConfigurationProgress;
  setupError: string | null;
  initialize(
    identity: TapToPaySetupIdentity,
    canConfigure: boolean | null,
    canAcceptPayments?: boolean | null
  ): Promise<void>;
  retryPersistence(): Promise<void>;
  acceptPointCraftTerms(): Promise<boolean>;
  setAppleLink(state: AppleLinkState): void;
  setEducation(state: EducationState): void;
  completeEducation(): Promise<boolean>;
  completeGuide(): Promise<boolean>;
  setReader(state: ReaderState): void;
  setConfiguration(progress: TapToPayConfigurationProgress): void;
  setSetupError(message: string | null): void;
};

function identityKey(identity: TapToPaySetupIdentity): string | null {
  const businessId = identity.businessId?.trim();
  const userId = identity.userId?.trim();
  const providerAccountId = identity.providerAccountId?.trim();
  return businessId && userId && providerAccountId
    ? `${businessId}:${userId}:${providerAccountId}`
    : null;
}

export function getTapToPaySetupAccountKey(identity: TapToPaySetupIdentity): string | null {
  const businessId = identity.businessId?.trim();
  const providerAccountId = identity.providerAccountId?.trim();
  return businessId && providerAccountId ? `${businessId}:${providerAccountId}` : null;
}

function authorizationOf(isAuthorized: boolean | null | undefined): TapToPayAuthorizationState {
  if (isAuthorized === null || isAuthorized === undefined) return 'authorization-unknown';
  return isAuthorized ? 'authorized' : 'unauthorized';
}

function authorizationBoolean(state: TapToPayAuthorizationState): boolean | null {
  if (state === 'authorization-unknown') return null;
  return state === 'authorized';
}

function persistenceFailureMessage(action: 'load' | 'save'): string {
  return action === 'load'
    ? 'PointCraft could not load the saved Tap to Pay guide status. Retry before continuing setup.'
    : 'PointCraft could not save the Tap to Pay guide status. Retry before leaving this screen.';
}

export const useTapToPaySetupStore = create<TapToPaySetupState>((set, get) => ({
  accountKey: null,
  identityKey: null,
  identity: {},
  authorization: 'authorization-unknown',
  paymentAuthorization: 'authorization-unknown',
  persistence: 'persistence-not-ready',
  persistenceError: null,
  guide: 'guide-required',
  pointcraftTerms: 'pointcraft-terms-required',
  appleLink: 'apple-link-unknown',
  education: 'education-not-started',
  reader: 'reader-idle',
  configuration: EMPTY_TAP_TO_PAY_CONFIGURATION,
  setupError: null,

  initialize: async (identity, canConfigure, canAcceptPayments = null) => {
    const nextAccountKey = getTapToPaySetupAccountKey(identity);
    const nextIdentityKey = identityKey(identity);
    const authorization = authorizationOf(canConfigure);
    const paymentAuthorization = authorizationOf(canAcceptPayments);
    const current = get();

    if (
      nextIdentityKey &&
      nextIdentityKey === current.identityKey &&
      nextAccountKey === current.accountKey &&
      current.persistence !== 'persistence-failed'
    ) {
      set({ authorization, paymentAuthorization });
      return;
    }

    const preserveSessionAuthority =
      nextAccountKey !== null && nextAccountKey === current.accountKey;

    if (!nextIdentityKey || !nextAccountKey) {
      set({
        accountKey: nextAccountKey,
        identityKey: nextIdentityKey,
        identity,
        authorization,
        paymentAuthorization,
        persistence: 'persistence-not-ready',
        persistenceError: null,
        guide: 'guide-required',
        pointcraftTerms: 'pointcraft-terms-required',
        education: 'education-not-started',
        ...(!preserveSessionAuthority
          ? {
              appleLink: 'apple-link-unknown' as const,
              reader: 'reader-idle' as const,
              configuration: EMPTY_TAP_TO_PAY_CONFIGURATION,
              setupError: null,
            }
          : {}),
      });
      return;
    }

    set({
      accountKey: nextAccountKey,
      identityKey: nextIdentityKey,
      identity,
      authorization,
      paymentAuthorization,
      persistence: 'persistence-loading',
      persistenceError: null,
      guide: 'guide-required',
      pointcraftTerms: 'pointcraft-terms-required',
      education: 'education-not-started',
      ...(!preserveSessionAuthority
        ? {
            appleLink: 'apple-link-unknown' as const,
            reader: 'reader-idle' as const,
            configuration: EMPTY_TAP_TO_PAY_CONFIGURATION,
            setupError: null,
          }
        : {}),
    });

    try {
      const persisted = await readTapToPaySetupRecord(identity);
      if (get().identityKey !== nextIdentityKey || get().accountKey !== nextAccountKey) return;

      set({
        persistence: 'persistence-ready',
        persistenceError: null,
        setupError: null,
        guide: persisted?.guideCompletedAt ? 'guide-completed' : 'guide-required',
        pointcraftTerms:
          persisted?.pointcraftTermsAcceptedAt &&
          persisted.pointcraftTermsVersion === POINTCRAFT_TAP_TO_PAY_TERMS_VERSION
            ? 'pointcraft-terms-accepted'
            : 'pointcraft-terms-required',
        education: persisted?.educationCompletedAt
          ? 'education-completed'
          : 'education-not-started',
      });
    } catch (error) {
      if (get().identityKey !== nextIdentityKey || get().accountKey !== nextAccountKey) return;
      console.error('[manager-pay] Failed to load Tap to Pay setup history:', error);
      const message = persistenceFailureMessage('load');
      set({
        persistence: 'persistence-failed',
        persistenceError: message,
        setupError: message,
      });
    }
  },
  retryPersistence: async () => {
    const state = get();
    await state.initialize(
      state.identity,
      authorizationBoolean(state.authorization),
      authorizationBoolean(state.paymentAuthorization)
    );
  },
  acceptPointCraftTerms: async () => {
    const expectedIdentityKey = get().identityKey;
    const { identity } = get();
    try {
      await recordPointCraftTermsAccepted(identity);
      if (get().identityKey !== expectedIdentityKey) return false;
      set({
        pointcraftTerms: 'pointcraft-terms-accepted',
        persistence: 'persistence-ready',
        persistenceError: null,
        setupError: null,
      });
      return true;
    } catch (error) {
      if (get().identityKey !== expectedIdentityKey) return false;
      console.error('[manager-pay] Failed to save PointCraft Tap to Pay terms:', error);
      const message = persistenceFailureMessage('save');
      set({ persistence: 'persistence-failed', persistenceError: message, setupError: message });
      return false;
    }
  },
  setAppleLink: (appleLink) => set({ appleLink }),
  setEducation: (education) => set({ education }),
  completeEducation: async () => {
    const expectedIdentityKey = get().identityKey;
    const { identity } = get();
    try {
      await recordMerchantEducationCompleted(identity);
      if (get().identityKey !== expectedIdentityKey) return false;
      set({
        education: 'education-completed',
        persistence: 'persistence-ready',
        persistenceError: null,
      });
      return true;
    } catch (error) {
      if (get().identityKey !== expectedIdentityKey) return false;
      console.error('[manager-pay] Failed to save Apple merchant education status:', error);
      set({ education: 'education-failed' });
      return false;
    }
  },
  completeGuide: async () => {
    const expectedIdentityKey = get().identityKey;
    const { identity } = get();
    try {
      await recordTapToPayGuideCompleted(identity);
      if (get().identityKey !== expectedIdentityKey) return false;
      set({
        guide: 'guide-completed',
        persistence: 'persistence-ready',
        persistenceError: null,
        setupError: null,
      });
      return true;
    } catch (error) {
      if (get().identityKey !== expectedIdentityKey) return false;
      console.error('[manager-pay] Failed to save Tap to Pay guide completion:', error);
      const message = persistenceFailureMessage('save');
      set({ persistence: 'persistence-failed', persistenceError: message, setupError: message });
      return false;
    }
  },
  setReader: (reader) => set({ reader }),
  setConfiguration: (configuration) => set({ configuration }),
  setSetupError: (setupError) => set({ setupError }),
}));

export function getTapToPayConnectionAuthorization(): TapToPayConnectionAuthorization {
  const state = useTapToPaySetupStore.getState();
  if (state.paymentAuthorization === 'authorized' && state.appleLink === 'apple-link-confirmed') {
    return 'session-linked';
  }
  return 'unauthorized';
}

export function isTapToPaySetupComplete(
  state: Pick<TapToPaySetupState, 'appleLink' | 'reader'>
): boolean {
  return state.appleLink === 'apple-link-confirmed' && state.reader === 'reader-ready';
}

export function shouldShowTapToPayFirstRunGuide(
  state: Pick<
    TapToPaySetupState,
    'authorization' | 'persistence' | 'guide' | 'pointcraftTerms' | 'appleLink'
  >
): boolean {
  if (state.authorization !== 'authorized') {
    return false;
  }

  // Keep the first-run surface mounted when persistence fails so its retry
  // action remains reachable. Loading/not-ready states wait for hydration and
  // never guess that onboarding is required.
  if (state.persistence === 'persistence-failed') return true;
  if (state.persistence !== 'persistence-ready') return false;

  // A live not-linked result is authoritative and overrides an old local
  // completion hint. Unknown/idle launch state does not.
  return (
    state.guide === 'guide-required' ||
    state.pointcraftTerms === 'pointcraft-terms-required' ||
    state.appleLink === 'apple-link-required'
  );
}

/** Clear all live authority and hydrated identity when the authenticated app session ends. */
export function resetTapToPaySetupSession(): void {
  useTapToPaySetupStore.setState({
    accountKey: null,
    identityKey: null,
    identity: {},
    authorization: 'authorization-unknown',
    paymentAuthorization: 'authorization-unknown',
    persistence: 'persistence-not-ready',
    persistenceError: null,
    guide: 'guide-required',
    pointcraftTerms: 'pointcraft-terms-required',
    appleLink: 'apple-link-unknown',
    education: 'education-not-started',
    reader: 'reader-idle',
    configuration: EMPTY_TAP_TO_PAY_CONFIGURATION,
    setupError: null,
  });
}
